import { CATEGORIES, entropy01 } from "./categories.js";

const GOLDEN = Math.PI * (3 - Math.sqrt(5));

export function categoryCenters(radius = 52) {
  const n = CATEGORIES.length;
  const centers = {};
  for (let i = 0; i < n; i += 1) {
    const y = 1 - (i / (n - 1)) * 2;
    const r = Math.sqrt(Math.max(0, 1 - y * y));
    const theta = GOLDEN * i;
    centers[CATEGORIES[i]] = {
      x: Math.cos(theta) * r * radius,
      y: y * radius * 0.62,
      z: Math.sin(theta) * r * radius,
    };
  }
  return centers;
}

export function layoutWords(rows, radius = 52) {
  const centers = categoryCenters(radius);
  const positions = new Float32Array(rows.length * 3);
  const phases = new Float32Array(rows.length);
  const sizes = new Float32Array(rows.length);
  const entropy = new Float32Array(rows.length);

  for (let i = 0; i < rows.length; i += 1) {
    const scores = rows[i].scores;
    let x = 0;
    let y = 0;
    let z = 0;
    let maxScore = 0;
    for (const key of CATEGORIES) {
      const w = (scores[key] || 0) / 100;
      if (w <= 0) continue;
      const c = centers[key];
      x += c.x * w;
      y += c.y * w;
      z += c.z * w;
      if (scores[key] > maxScore) maxScore = scores[key];
    }
    const h = entropy01(scores);
    const spread = 1.15 + h * 11.5;
    const seed = hash32(rows[i].word) / 4294967296;
    const seed2 = hash32(`${rows[i].word}:y`) / 4294967296;
    const seed3 = hash32(`${rows[i].word}:z`) / 4294967296;
    x += gauss(seed) * spread;
    y += gauss(seed2) * spread * 0.72;
    z += gauss(seed3) * spread;
    positions[i * 3] = x;
    positions[i * 3 + 1] = y;
    positions[i * 3 + 2] = z;
    phases[i] = seed * Math.PI * 2;
    sizes[i] = 1.15 + (maxScore / 100) * 2.4 + h * 0.55;
    entropy[i] = h;
  }

  return { positions, phases, sizes, entropy, centers };
}

export function buildLinks(rows, positions, entropy, maxNeighbors = 3) {
  const groups = {};
  for (const key of CATEGORIES) groups[key] = [];
  for (let i = 0; i < rows.length; i += 1) {
    let best = CATEGORIES[0];
    let bestValue = -1;
    for (const key of CATEGORIES) {
      const value = rows[i].scores[key] || 0;
      if (value > bestValue) {
        best = key;
        bestValue = value;
      }
    }
    groups[best].push(i);
  }

  const edges = new Set();
  for (const key of CATEGORIES) {
    addNearestEdges(groups[key], positions, maxNeighbors, edges);
  }

  for (let i = 0; i < rows.length; i += 1) {
    if (entropy[i] < 0.22) continue;
    const ranked = CATEGORIES
      .map((key) => [key, rows[i].scores[key] || 0])
      .sort((a, b) => b[1] - a[1]);
    const top = ranked.filter((entry) => entry[1] >= 8).slice(0, 3);
    for (const [key] of top) {
      const neighbor = nearestInGroup(i, groups[key], positions);
      if (neighbor >= 0) addEdge(i, neighbor, edges);
    }
  }

  const pairs = [];
  for (const key of edges) {
    const [a, b] = key.split(":").map(Number);
    pairs.push(a, b);
  }
  return pairs;
}

function addNearestEdges(indices, positions, k, edges) {
  const n = indices.length;
  for (let a = 0; a < n; a += 1) {
    const ia = indices[a];
    const ax = positions[ia * 3];
    const ay = positions[ia * 3 + 1];
    const az = positions[ia * 3 + 2];
    const best = [];
    for (let b = 0; b < n; b += 1) {
      if (a === b) continue;
      const ib = indices[b];
      const dx = ax - positions[ib * 3];
      const dy = ay - positions[ib * 3 + 1];
      const dz = az - positions[ib * 3 + 2];
      const d2 = dx * dx + dy * dy + dz * dz;
      if (best.length < k) {
        best.push({ ib, d2 });
        best.sort((p, q) => p.d2 - q.d2);
      } else if (d2 < best[k - 1].d2) {
        best[k - 1] = { ib, d2 };
        best.sort((p, q) => p.d2 - q.d2);
      }
    }
    for (const item of best) addEdge(ia, item.ib, edges);
  }
}

function nearestInGroup(index, group, positions) {
  const ax = positions[index * 3];
  const ay = positions[index * 3 + 1];
  const az = positions[index * 3 + 2];
  let best = -1;
  let bestD = Infinity;
  for (const ib of group) {
    if (ib === index) continue;
    const dx = ax - positions[ib * 3];
    const dy = ay - positions[ib * 3 + 1];
    const dz = az - positions[ib * 3 + 2];
    const d2 = dx * dx + dy * dy + dz * dz;
    if (d2 < bestD) {
      bestD = d2;
      best = ib;
    }
  }
  return best;
}

function addEdge(a, b, edges) {
  const lo = a < b ? a : b;
  const hi = a < b ? b : a;
  edges.add(`${lo}:${hi}`);
}

function hash32(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function gauss(u) {
  const v = Math.max(1e-6, u);
  return Math.sqrt(-2 * Math.log(v)) * Math.cos(v * Math.PI * 2) * 0.55;
}
